import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import { detect } from "detect-browser";
import { formatRelative } from "date-fns";
import { enGB } from "date-fns/locale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbsUp,
  faHandshake,
  faUsers,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Wrapper,
  Column,
  Top,
  FeedbackOverdue,
  Badge,
  BadgeTooltip,
  Bottom,
  BottomItem,
  Divider,
  Number,
  DotContainer,
  Dot,
  JobTitleLink,
  JobTitleSpan,
  KanbanLink,
  KanbanSpan,
  NumberOfPositions,
  NumberOfPositionsPlaceholder,
} from "./styles/job-styles";
import JobActionColumn from "./JobActionColumn";
import jobType from "./types/jobType";

export const Job = ({
  feedbacks,
  job,
  style,
  isRootParentEmployer,
  onDelete,
}) => {
  const handleTitleLength = (jobTitle) => {
    if (jobTitle.length > 35 && window.innerWidth < 380) {
      return `${jobTitle.slice(0, 35)}...`;
    }
    return jobTitle;
  };

  const feedbacksForJob = feedbacks.filter(
    (feedback) => feedback.jobId === parseInt(job.id, 10)
  );

  const multiplePostionsTooltip = (acceptedOffers, positionsCount) => `
      <strong>${acceptedOffers} offers</strong> have been accepted out<br>
      of the <strong>${positionsCount} positions</strong>
    `;

  const pendingEngagementsCount = job.engagements.filter(
    (engagement) => engagement.status === "pending"
  ).length;

  const acceptedEngagementsCount = job.engagements.filter(
    (engagement) => engagement.status === "accepted"
  ).length;

  // applications are pre-scoped to ones that have feedback overdue
  const hasFeedbackOverdue = job.applications.length > 0;

  const engToolTip = `
      <strong>Engagements:</strong><br>
      ${pendingEngagementsCount} pending<br>
      ${acceptedEngagementsCount} accepted
    `;

  const appsToolTip = `
      <strong>Applications:</strong><br>
      ${job.unreadApplicationsCount} unread<br>
      ${job.readApplicationsCount} read
    `;

  const underReviewTooltipText =
    "Your job is under review, which means your<br>" +
    "account manager needs to qualify the<br>" +
    "role before it can go to our agencies.<br>" +
    "To accelerate this process, please<br>" +
    "contact your account manager.";

  const isIE = detect().name === "ie";

  const engagementsBottomItem = (
    <BottomItem
      numbers
      data-html
      data-tip={engToolTip}
      data-for={`${job.id}-counts-tooltip`}
    >
      <Number
        highlight={
          job.engagements.filter((engagement) => engagement.status == "pending")
            .length > 0
        }
      >
        {pendingEngagementsCount}
      </Number>
      <Number>{acceptedEngagementsCount}</Number>
    </BottomItem>
  );

  const applicationsBottomItem = (
    <BottomItem
      numbers
      data-html
      data-tip={appsToolTip}
      data-for={`${job.id}-counts-tooltip`}
    >
      <Number highlight={job.hasNewApplications}>
        {job.unreadApplicationsCount}
      </Number>
      <Number>{job.readApplicationsCount}</Number>
    </BottomItem>
  );

  return (
    <Wrapper style={style}>
      <Column isIE={isIE}>
        <Top titleField feedbackOverdue>
          <strong>
            {isRootParentEmployer ? (
              <JobTitleSpan title="Job Profile">
                {handleTitleLength(job.title)}
              </JobTitleSpan>
            ) : (
              <JobTitleLink href={job.jobHubJobProfilePath} title="Job Profile">
                {handleTitleLength(job.title)}
              </JobTitleLink>
            )}
            <ReactTooltip
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id={`${job.id}-feedback`}
            />
            {hasFeedbackOverdue && (
              <FeedbackOverdue
                data-tip="Feedback is overdue"
                data-for={`${job.id}-feedback`}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </FeedbackOverdue>
            )}
            {job.exclusive && <Badge>Exclusive</Badge>}
            {job.publishedStatus === "under_review" && (
              <Badge
                data-test-id={`under-review-badge-${job.id}`}
                data-tip={underReviewTooltipText}
                data-for={`under-review-tooltip-${job.id}`}
              >
                Under Review
              </Badge>
            )}
            {job.publishedStatus === "draft" && (
              <Badge data-test-id={`draft-badge-${job.id}`}>Draft</Badge>
            )}
          </strong>
          <BadgeTooltip>
            <ReactTooltip
              multiline
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id={`under-review-tooltip-${job.id}`}
            />
          </BadgeTooltip>
        </Top>
        <Bottom desktopStack location>
          <BottomItem>
            {
              formatRelative(Date.parse(job.createdAt), new Date(), {
                locale: enGB,
              }).split(" at")[0]
            }
          </BottomItem>
          <Divider>|</Divider>
          <BottomItem>{job.location}</BottomItem>
        </Bottom>
      </Column>
      <Column flexSize={2} hideOnTablet isIE={isIE} ieWidth={isIE}>
        <Top />
        <Bottom desktopStack>
          <BottomItem>
            <strong>{job.currency.symbol} </strong>
            {job.salary}
          </BottomItem>
          <BottomItem>{job.placementFeePercent}%</BottomItem>
        </Bottom>
      </Column>
      <Column flexSize={2} capitalize hideOnTablet isIE={isIE} ieWidth={isIE}>
        <Top />
        <Bottom desktopStack>
          <BottomItem>{job.jobType}</BottomItem>
          <BottomItem offerMade={job.status === "offer_made"}>
            {job.status.replace("_", " ")}
          </BottomItem>
        </Bottom>
      </Column>
      {job.publishedStatus !== "draft" && (
        <Column flexSize={1} hideOnTablet isIE={isIE} ieWidth={isIE} center>
          <Top>
            {job.numberOfPositions > 1 ? (
              <NumberOfPositions>
                <FontAwesomeIcon icon={faThumbsUp} />
              </NumberOfPositions>
            ) : (
              <NumberOfPositionsPlaceholder />
            )}
            <FontAwesomeIcon icon={faHandshake} />
            <FontAwesomeIcon icon={faUsers} />
          </Top>
          <Bottom desktopStack numbers>
            <ReactTooltip
              html
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id={`${job.id}-counts-tooltip`}
            />
            {job.numberOfPositions > 1 ? (
              <NumberOfPositions
                data-tip={multiplePostionsTooltip(
                  job.placements.length,
                  job.numberOfPositions
                )}
                data-for={`${job.id}-counts-tooltip`}
              >
                {job.placements.length}/{job.numberOfPositions}
              </NumberOfPositions>
            ) : (
              <NumberOfPositionsPlaceholder />
            )}
            {isRootParentEmployer ? (
              <>
                <KanbanSpan data-test-id="engagements-span">
                  {engagementsBottomItem}
                </KanbanSpan>
                <KanbanSpan data-test-id="applications-span">
                  {applicationsBottomItem}
                </KanbanSpan>
              </>
            ) : (
              <>
                <KanbanLink
                  href={job.jobHubAgentsPath}
                  data-test-id="engagements-link"
                >
                  {engagementsBottomItem}
                </KanbanLink>
                <KanbanLink
                  href={job.jobHubCandidatesPath}
                  data-test-id="applications-link"
                >
                  {applicationsBottomItem}
                </KanbanLink>
              </>
            )}
          </Bottom>
        </Column>
      )}
      <Column flexSize="unset" hideOnDesktop>
        {(job.hasNewApplications || job.hasNewEngagements) && (
          <DotContainer mobileDot>
            <Dot pink offerMade={job.status === "offer_made"} />
          </DotContainer>
        )}
      </Column>
      {!isRootParentEmployer && (
        <JobActionColumn
          job={job}
          feedbacksForJob={feedbacksForJob}
          onDelete={onDelete}
        />
      )}
    </Wrapper>
  );
};

Job.propTypes = {
  job: jobType.isRequired,
  feedbacks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  style: PropTypes.shape({}).isRequired,
  isRootParentEmployer: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  feedbacks: state.jobsIndex.feedbacks,
});

export default connect(mapStateToProps, {})(Job);
